import { useParams, useNavigate } from "react-router-dom";
import { Button, SimpleGrid, HStack, Text, Skeleton, Box, useDisclosure } from "@chakra-ui/react";
import { PageHeader, SectionCard, StatCard, DataTable, StatusBadge, CurrencyText, EmptyState } from "../../../shared/ui";
import { ChevronLeft, FileText, Wallet, Check, Clock, AlertTriangle } from "../../../shared/icons";
import { useVinculoQuery } from "../hooks/useVinculoQuery";
import { rotuloTipoVinculo } from "../api/vinculosApi";
import { formatBRL } from "../../../shared/lib/currency";
import ShiftPatternsCard from "../../shiftPatterns/components/ShiftPatternsCard";
import GenerateScheduleModal from "../../shiftPatterns/components/GenerateScheduleModal";

const COLUNAS = [
  { key: "data", label: "Data" },
  { key: "descricao", label: "Descrição" },
  { key: "valor", label: "Valor", align: "right", render: (row) => <CurrencyText value={row.valor} /> },
  { key: "previsao", label: "Previsão" },
  { key: "status", label: "Status", render: (row) => <StatusBadge status={row.status} /> },
];

export default function VinculoDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useVinculoQuery(id);

  const vinculo = data?.vinculo;
  const plantoes = data?.plantoes ?? [];
  const gerarEscala = useDisclosure();
  const hoje = new Date();

  return (
    <>
      <Button variant="ghost" size="sm" leftIcon={<ChevronLeft size={16} />} onClick={() => navigate("/vinculos")} mb={4}>
        Voltar para vínculos
      </Button>

      {isLoading || !vinculo ? (
        <Skeleton height="120px" borderRadius="xl" mb={6} />
      ) : (
        <PageHeader
          title={vinculo.nome}
          description={`${rotuloTipoVinculo(vinculo.tipo)}${vinculo.cnpj ? ` · CNPJ ${vinculo.cnpj}` : ""} · ${formatBRL(vinculo.valor_plantao)} por plantão${
            vinculo.valor_fim_de_semana != null ? ` · Fim de semana ${formatBRL(vinculo.valor_fim_de_semana)}` : ""
          } · Paga dia ${vinculo.dia_pagamento} · NF até dia ${vinculo.dia_limite_nf}`}
          actions={
            <Button variant="secondary" leftIcon={<FileText size={16} />} onClick={() => navigate(`/relatorios?vinculo=${vinculo.id}`)}>
              Gerar comprovação
            </Button>
          }
        />
      )}

      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4} mb={6}>
        <SectionCard p={0}>
          <StatCard label="No mês" value={vinculo?.total_no_mes ?? 0} icon={Wallet} tone="brand" isLoading={isLoading} footnote={vinculo ? `${vinculo.plantoes_no_mes} plantão(ões)` : undefined} />
        </SectionCard>
        <SectionCard p={0}>
          <StatCard label="Recebido" value={vinculo?.recebido ?? 0} icon={Check} tone="success" isLoading={isLoading} />
        </SectionCard>
        <SectionCard p={0}>
          <StatCard label="A receber" value={vinculo?.a_receber ?? 0} icon={Clock} tone="teal" isLoading={isLoading} />
        </SectionCard>
        <SectionCard p={0}>
          <StatCard label="Em atraso" value={vinculo?.em_atraso ?? 0} icon={AlertTriangle} tone="danger" isLoading={isLoading} footnote={vinculo?.plantoes_atraso ? `${vinculo.plantoes_atraso} plantão(ões)` : undefined} />
        </SectionCard>
      </SimpleGrid>

      <ShiftPatternsCard vinculoId={id} onGenerate={gerarEscala.onOpen} />

      <SectionCard title="Plantões deste vínculo">
        <DataTable
          columns={COLUNAS}
          rows={plantoes}
          isLoading={isLoading}
          emptyState={
            <EmptyState title="Nenhum plantão registrado" description="Registre o primeiro plantão deste vínculo pelo botão no topo da página." />
          }
          footer={
            plantoes.length > 0 ? (
              <Box borderTop="1px solid" borderColor="app-border" mt={2} pt={3}>
                <HStack justify="space-between">
                  <Text fontSize="sm" color="app-muted-fg">
                    {plantoes.length} plantão(ões)
                  </Text>
                  <Text fontSize="sm" fontWeight="semibold">
                    <CurrencyText value={plantoes.reduce((acc, p) => acc + p.valor, 0)} />
                  </Text>
                </HStack>
              </Box>
            ) : null
          }
        />
      </SectionCard>

      <GenerateScheduleModal
        isOpen={gerarEscala.isOpen}
        onClose={gerarEscala.onClose}
        initialYear={hoje.getFullYear()}
        initialMonth={hoje.getMonth() + 1}
      />
    </>
  );
}
